import cn from 'classnames'
import { FC, useEffect, useMemo, useRef, useState } from 'react'

import { RenderWhen } from 'clickNgo/components/renderWhen'
import { NumberInput } from 'cng-common/components/numberInput'
import { INumberInputProps } from 'cng-common/components/numberInput/models'
import { formatSobValue } from 'partner360/lib/metricFormatting'

import styles from './FloatingNumberInput.module.scss'

interface FloatingNumberInputProps extends Omit<INumberInputProps, 'ref'> {
  floatLength?: number
  className?: string
}

export const FloatingNumberInput: FC<FloatingNumberInputProps> = ({
  floatLength = 10,
  onBlur,
  onFocus,
  className,
  ...props
}) => {
  const [isFocused, setIsFocused] = useState(false)
  const ref = useRef<HTMLInputElement>(null)

  const displayValue = useMemo(() => {
    const rawValue = props.value
    if (rawValue === '' || rawValue === null || rawValue === undefined) {
      return ''
    }

    return formatSobValue(Number(rawValue)) ?? ''
  }, [props.value])

  const width = useMemo(() => {
    if (displayValue.length <= floatLength) {
      return undefined
    }

    return `${displayValue.length / 1.6}em`
  }, [displayValue, floatLength])

  useEffect(() => {
    if (isFocused) {
      ref.current?.focus()
    }
  }, [isFocused])

  return (
    <div className={styles.floatingNumberInputContainer}>
      <NumberInput
        {...props}
        classes={{
          ...props.classes,
          inputClasses: {
            ...props.classes?.inputClasses,
            root: cn(props.classes?.inputClasses?.root, className, 'floating-number-input'),
          },
        }}
        onFocus={() => setIsFocused(true)}
        value={props.value}
        align={props.align}
        disabled={props.disabled}
        maximumFractionDigits={props.maximumFractionDigits}
        checkFn={props.checkFn}
      />

      <RenderWhen condition={isFocused}>
        <div className={styles.floatingContainer} style={{ width }}>
          <NumberInput
            ref={ref as any}
            classes={{
              inputClasses: {
                root: cn(className, 'floating-number-input'),
              },
            }}
            onFocus={() => {
              onFocus?.()
            }}
            onBlur={(event) => {
              setIsFocused(false)
              onBlur?.(event)
            }}
            {...props}
          />
        </div>
      </RenderWhen>
    </div>
  )
}
